<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col sm="12" md="6">
        <b-card header="Channel selection">
          <h4 class="smalltitle">Welcome channel</h4>
          <hr />
          Useful variables:
          <br />
          <code>{mention}</code> - Mentions the person joining/leaving.
          <br />
          <code>{server}</code> - The server's name
          <br />
          <code>{user(proper)}</code> - The person joining/leaving's name in the
          'Galactus' format.
          <br />
          <code>{server(members)}</code> - The total number of members after the
          event has happened.
          <br />
          <a href="https://docs.carl.gg/#/tagstriggers?id=advanced-usage"
            >Tagscript</a
          >
          is partially supported, use
          <code>{random: first message ~ second message}</code> for a randomized
          message (separated by ~) or <code>{ord: {server(members)}}</code> to
          transform <code>8102</code> to
          <code>8,102nd</code>
          <hr />

          <label for="discord_channel_select" class="teenytinytitle"
            >Welcome Channel</label
          >
          <multiselect
            v-model="greet_channel"
            id="discord_channel_select"
            track-by="id"
            label="name"
            placeholder="Please select a channel"
            open-direction="bottom"
            :searchable="true"
            :clear-on-select="false"
            :close-on-select="true"
            :max-height="600"
            :show-no-results="false"
            :hide-selected="false"
            :options="groupedChannels"
            :allow-empty="true"
            group-values="channels"
            group-label="category"
            :group-select="false"
          >
            <template slot="singleLabel" slot-scope="props">
              <span class="option__desc">
                <font-awesome-icon style="color: #72767d" icon="hashtag" />
                <span class="option__title ml-2">{{ props.option.name }}</span>
              </span>
            </template>
            <template slot="option" slot-scope="props">
              <div class="option__desc">
                <span class="option__title">
                  <font-awesome-icon
                    style="color: #72767d"
                    :icon="
                      !props.option['$isLabel'] ? 'hashtag' : 'chevron-down'
                    "
                  />
                  {{ props.option.name || props.option.$groupLabel.name }}
                </span>
              </div>
            </template>
          </multiselect>

          <hr />

          <label for="farewell_channel_select" class="teenytinytitle">
            Farewell Channel
            <a href="/get-premium" v-if="!premium" class="premium-pill">
              <i class="fas fa-crown"></i> Premium
            </a>
          </label>
          <p v-if="!premium" class="premium-hint">
            Send a separate goodbye message when members leave, keep your welcome and farewell flows in different channels.
            <a href="/get-premium">Unlock with Premium →</a>
          </p>
          <multiselect
            v-model="farewell_channel"
            id="farewell_channel_select"
            track-by="id"
            label="name"
            placeholder="Please select a channel"
            open-direction="bottom"
            :searchable="true"
            :clear-on-select="false"
            :close-on-select="true"
            :max-height="600"
            :show-no-results="false"
            :hide-selected="false"
            :options="groupedChannels"
            :allow-empty="true"
            group-values="channels"
            group-label="category"
            :group-select="false"
            :disabled="!premium"
          >
            <template slot="singleLabel" slot-scope="props">
              <span class="option__desc">
                <font-awesome-icon style="color: #72767d" icon="hashtag" />
                <span class="option__title ml-2">{{ props.option.name }}</span>
              </span>
            </template>
            <template slot="option" slot-scope="props">
              <div class="option__desc">
                <span class="option__title">
                  <font-awesome-icon
                    style="color: #72767d"
                    :icon="
                      !props.option['$isLabel'] ? 'hashtag' : 'chevron-down'
                    "
                  />
                  {{ props.option.name || props.option.$groupLabel.name }}
                </span>
              </div>
            </template>
          </multiselect>

          <div slot="footer">
            <b-button variant="primary" v-on:click="put_settings"
              >Save</b-button
            >
          </div>
        </b-card>
        <b-card>
          <div slot="header"><i class="icon-logout"></i> Goodbye Message</div>
          <h5 class="smalltitle">Leave message</h5>
          <hr />
          <b-input-group>
            <b-form-textarea
              :maxlength="5000"
              rows="6"
              type="text"
              placeholder="It seems {user} has left us... We are now {server(members)}"
              class="col-12"
              v-model="farewell_message"
            ></b-form-textarea>
          </b-input-group>
          <EmbedBuilder ref="farewell_embed"></EmbedBuilder>
          <div slot="footer">
            <b-button variant="primary" v-on:click="put_settings"
              >Save</b-button
            >
            <b-button
              variant="ghost-danger"
              class="ml-2"
              v-on:click="farewell_message = ''"
              >Clear</b-button
            >
          </div>
        </b-card>
        <b-card>
          <div slot="header"><i class="icon-shield"></i> Ban Message</div>
          <h5 class="smalltitle">
            Ban message
            <font-awesome-icon
              style="color: #888"
              :icon="['fas', 'question-circle']"
              class="mx-2"
              id="restricted_question"
            />
            <b-popover
              triggers="hover"
              placement="top"
              target="restricted_question"
            >
              <h5 class="tinytitle">What is a ban message?</h5>
              It is <i>not</i> a message that gets sent to the banned member. It
              is a message sent to the predetermined channel when a ban occurs.
            </b-popover>
          </h5>
          <hr />
          <b-input-group>
            <b-form-textarea
              :maxlength="5000"
              rows="6"
              type="text"
              placeholder="{user} flew too close to the sun and got banned. Owned."
              class="col-12"
              v-model="ban_message"
            ></b-form-textarea>
          </b-input-group>
          <EmbedBuilder ref="ban_embed"></EmbedBuilder>
          <div slot="footer">
            <b-button variant="primary" v-on:click="put_settings"
              >Save</b-button
            >
            <b-button
              variant="ghost-danger"
              class="ml-2"
              v-on:click="ban_message = ''"
              >Clear</b-button
            >
          </div>
        </b-card>
      </b-col>
      <b-col sm="12" md="6">
        <b-card>
          <div slot="header"><i class="icon-login"></i> Welcome Message</div>
          <h5 class="smalltitle">Welcome message</h5>
          <hr />
          <b-input-group>
            <b-form-textarea
              :maxlength="5000"
              :rows="6"
              type="text"
              placeholder="Hello {mention} and welcome to {server}."
              class="col-12"
              v-model="greet_message"
            ></b-form-textarea>
          </b-input-group>
          <EmbedBuilder ref="greet_embed"></EmbedBuilder>

          <div slot="footer">
            <b-button variant="primary" v-on:click="put_settings"
              >Save</b-button
            >
            <b-button
              variant="ghost-danger"
              class="ml-2"
              v-on:click="greet_message = ''"
              >Clear</b-button
            >
          </div>
        </b-card>
        <b-card>
          <div slot="header">
            <i class="icon-envelope"></i> DM on join message
          </div>
          <h5 class="smalltitle">DM on join</h5>
          <hr />
          <b-input-group>
            <b-form-textarea
              :maxlength="2000"
              rows="6"
              type="text"
              placeholder="Howdy {user} and welcome to {server}! Make sure to check out #rules before gaining access to the server."
              class="col-12"
              v-model="dm_message"
            ></b-form-textarea>
          </b-input-group>
          <EmbedBuilder ref="dm_embed"></EmbedBuilder>
          <div slot="footer">
            <b-button variant="primary" v-on:click="put_settings"
              >Save</b-button
            >
            <b-button
              variant="ghost-danger"
              class="ml-2"
              v-on:click="dm_message = ''"
              >Clear</b-button
            >
          </div>
        </b-card>
        <b-card>
  <div slot="header">
    <i class="icon-present"></i> Birthday
    <a
      href="/get-premium"
      v-if="!premium"
      class="premium-pill"
    >
      <i class="fas fa-crown"></i> Premium
    </a>
  </div>

  <!-- PREMIUM -->
  <h4 class="smalltitle">
    Birthday Message
  </h4>

  <p
    v-if="!premium"
    class="premium-hint"
  >
    Customize birthday announcements with your own message.
    <a href="/get-premium">
      Unlock with Premium →
    </a>
  </p>

  <b-input-group>
    <b-form-textarea
      :maxlength="2000"
      rows="3"
      class="col-12"
      placeholder="🎉 Happy Birthday {mention}!"
      v-model="birthday_message"
      :disabled="!premium"
    />
  </b-input-group>
<div
  :style="!premium ? 'opacity:0.5;pointer-events:none;' : ''"
>
  <EmbedBuilder ref="birthday_embed"></EmbedBuilder>
</div>
  <hr />

  <h4 class="smalltitle">
    Announcement Time
  </h4>

  <p
    v-if="!premium"
    class="premium-hint"
  >
    Choose exactly when birthday announcements are sent.
    <a href="/get-premium">
      Unlock with Premium →
    </a>
  </p>

  <b-form-select
    v-model="announcement_hour"
    :disabled="!premium"
    :options="birthdayHourOptions"
  />

  <hr />

  <!-- PREMIUM -->
  <h4 class="smalltitle">
    Birthday Role
  </h4>

  <p
    v-if="!premium"
    class="premium-hint"
  >
    Automatically assign a special birthday role to members on their birthday.
    <a href="/get-premium">
      Unlock with Premium →
    </a>
  </p>

  <multiselect
    v-model="birthday_role"
    track-by="id"
    label="name"
    placeholder="Select role"
    :options="roles"
    :allow-empty="true"
    :disabled="!premium"
  />
  <hr />

<!-- PREMIUM -->
<div class="form-group">
    <div class="d-flex align-items-center">
        <h4 class="smalltitle mb-0 mr-2">
            Combine birthday messages
        </h4>

       <c-switch
          color="info"
          v-model="combineAlerts"
          :disabled="!premium"
          variant="pill"
        />
    </div>

    <p
        v-if="!premium"
        class="premium-hint"
    >
        Send one announcement when multiple users have birthdays on the same day.
        <a href="/get-premium">
            Unlock with Premium →
        </a>
    </p>

    <small
        v-else
        class="form-text text-muted"
    >
        Send one announcement when multiple users have birthdays on the same day.
    </small>
</div>

  <div slot="footer">
    <b-button
      variant="primary"
      v-on:click="saveBirthday"
    >
      Save
    </b-button>

    <b-button
      variant="ghost-danger"
      class="ml-2"
      v-on:click="
        birthday_message = '🎉 Happy Birthday {mention}!';
        announcement_hour = 12;
        birthday_role = null;
      "
    >
      Reset
    </b-button>
  </div>
</b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import Vue from "vue";
import { Switch as cSwitch } from "@coreui/vue";
import { Callout } from "@coreui/vue";
import VueNotifications from "vue-notifications";
import miniToastr from "mini-toastr";
import EmbedBuilder from "@/views/oc/EmbedBuilder";
import Multiselect from "vue-multiselect";

const toastTypes = {
  success: "success",
  error: "error",
  info: "info",
  warn: "warn",
};
miniToastr.init({ types: toastTypes });

function toast({ title, message, type, timeout, cb }) {
  return miniToastr[type](message, title, timeout, cb);
}

const toast_options = {
  success: toast,
  error: toast,
  info: toast,
  warn: toast,
};

const reducer = (accumulator, currentValue, currentIndex) =>
  accumulator + (currentValue << currentIndex);

Vue.use(VueNotifications, toast_options);

export default {
  name: "Logging",
  components: {
    cSwitch,
    EmbedBuilder,
    Multiselect,
  },
  data: function () {
    return {
      labelIcon: {
        dataOn: "\u2713",
        dataOff: "\u2715",
      },
      channels: [],
      combineAlerts: false,
      greet_channel: null,
      farewell_channel: null,
      greet_message: "",
      greet_embed: {},
      farewell_message: "",
      farewell_embed: {},
      dm_message: "",
      dm_embed: {},
      ban_message: "",
      ban_embed: {},
      premium: false,
      birthday_message: "",
      birthday_role: null,
      announcement_hour: "12:00",
      roles: [],
      birthday_embed: {},
    };
  },
  notifications: {
    showSuccessMsg: {
      type: VueNotifications.types.success,
      title: "Success!",
      message: "Setting applied",
    },
  },
  beforeRouteUpdate(to, from, next) {
    next();
    this.axios
      .get("/api/v1/servers/" + this.$route.params.guild_id + "/welcome")
      .then((r) => {
        this.channels = r.data.channels;
        this.greet_channel = this.channels.find(
          (x) => x.id == r.data.greet_channel
        ) || { name: "No channel selected", id: null };
        this.premium = r.data.premium;
        if (this.premium) {
          this.farewell_channel = this.channels.find(
            (x) => x.id == r.data.farewell_channel
          ) || { name: "No channel selected", id: null };
        } else {
          this.farewell_channel = { name: "No channel selected", id: null };
        }
        this.greet_message = r.data.greet_message;
        this.farewell_message = r.data.farewell_message;
        this.dm_message = r.data.dm_message;
        this.ban_message = r.data.ban_message;
        this.$refs.greet_embed.apply_json(r.data.greet_embed);
        this.$refs.farewell_embed.apply_json(r.data.farewell_embed);
        this.$refs.ban_embed.apply_json(r.data.ban_embed);
        this.$refs.dm_embed.apply_json(r.data.dm_embed);
      });
      this.axios
  .get("/api/v1/servers/" + this.$route.params.guild_id + "/birthday")
  .then((r) => {
    this.roles = r.data.roles || [];

    this.birthday_message =
      r.data.announcement_message ||
      "🎉 Happy Birthday {mention}!";
      this.combineAlerts = !!r.data.combine_alerts;
    this.$refs.birthday_embed.apply_json(
    JSON.parse(r.data.announcement_embed));


    this.birthday_role =
      this.roles.find(
        (x) => x.id == r.data.birthday_role
      ) || null;
      this.announcement_hour =
  r.data.announcement_hour != null
    ? parseInt(r.data.announcement_hour.split(":")[0], 10)
    : 12;
  });
  },
  methods: {
    put_settings() {
      this.axios
        .put("/api/v1/servers/" + this.$route.params.guild_id + "/welcome", {
          greet_channel: this.greet_channel ? this.greet_channel.id : null,
          farewell_channel:
            this.premium && this.farewell_channel
              ? this.farewell_channel.id
              : null,
          greet_message: this.greet_message,
          farewell_message: this.farewell_message,
          dm_message: this.dm_message,
          ban_message: this.ban_message,
          greet_embed: this.$refs.greet_embed.full_json,
          farewell_embed: this.$refs.farewell_embed.full_json,
          ban_embed: this.$refs.ban_embed.full_json,
          dm_embed: this.$refs.dm_embed.full_json,
        })
        .then(this.showSuccessMsg());
    },
    saveBirthday() {
  this.axios
    .put(
      "/api/v1/servers/" + this.$route.params.guild_id + "/birthday",
      {
        announcement_message: this.birthday_message,
        announcement_embed: this.$refs.birthday_embed.full_json,
        birthday_role: this.birthday_role
          ? this.birthday_role.id
          : null,
         combine_alerts: this.combineAlerts,

        announcement_hour:
          this.announcement_hour,
      }
    )
    .then(this.showSuccessMsg());
},
  },
  computed: {
  birthdayHourOptions() {
  return Array.from({ length: 24 }, (_, i) => ({
    value: i,
    text: `${String(i).padStart(2, "0")}:00 UTC`,
  }));
},
    groupedChannels() {
      let groups = [
        {
          category: {
            id: null,
            name: "Uncategorized",
          },
          channels: [],
        },
      ];
      let currentGroup = groups[0];
      for (let chn of this.channels.sort((a, b) => a.position - b.position)) {
        if (chn.type === 4) {
          currentGroup = { category: chn, channels: [] };
          groups.push(currentGroup);
        }
      }
      for (let chn of this.channels.sort((a, b) => a.position - b.position)) {
        if (chn.type === 4) {
          continue;
        }
        currentGroup = groups.find((g) => {
          return g.category.id == chn.parent_id;
        });
        if (!currentGroup) {
          continue;
        }
        currentGroup.channels.push(chn);
      }
      return groups;
    },
  },
  created() {
    this.$Progress.start();
    this.axios
      .get("/api/v1/servers/" + this.$route.params.guild_id + "/welcome")
      .then((r) => {
        this.channels = r.data.channels;
        this.greet_channel = this.channels.find(
          (x) => x.id == r.data.greet_channel
        ) || { name: "No channel selected", id: null };
        this.premium = r.data.premium;
        if (this.premium) {
          this.farewell_channel = this.channels.find(
            (x) => x.id == r.data.farewell_channel
          ) || { name: "No channel selected", id: null };
        } else {
          this.farewell_channel = { name: "No channel selected", id: null };
        }
        this.greet_message = r.data.greet_message;
        this.farewell_message = r.data.farewell_message;
        this.dm_message = r.data.dm_message;
        this.ban_message = r.data.ban_message;
        this.$refs.greet_embed.apply_json(JSON.parse(r.data.greet_embed));
        this.$refs.farewell_embed.apply_json(JSON.parse(r.data.farewell_embed));
        this.$refs.ban_embed.apply_json(JSON.parse(r.data.ban_embed));
        this.$refs.dm_embed.apply_json(JSON.parse(r.data.dm_embed));
        this.premium = r.data.premium;
        this.$forceUpdate();
        this.$Progress.finish();
      });
      this.axios
  .get("/api/v1/servers/" + this.$route.params.guild_id + "/birthday")
  .then((r) => {
    this.birthday_message = r.data.announcement_message;
    this.combineAlerts = !!r.data.combine_alerts;
    this.$refs.birthday_embed.apply_json(
    JSON.parse(r.data.announcement_embed));
    this.roles = r.data.roles || [];

    this.birthday_role =
      this.roles.find(
        (x) => x.id == r.data.birthday_role
      ) || null;
      this.announcement_hour =
  r.data.announcement_hour != null
    ? parseInt(r.data.announcement_hour.split(":")[0], 10)
    : 12;
  });
  },
};
</script>
<style scoped>
.multiselect--disabled {
  background-color: #4a5568 !important;
  border-radius: 0.375rem !important;
}

.multiselect--disabled .multiselect__tags {
  background-color: #4a5568 !important;
  border: 1px solid #4a5568 !important;
}

.multiselect--disabled .multiselect__single {
  background-color: transparent !important;
  color: #a0aec0 !important;
}

/* Remove any corner artifacts */
.multiselect--disabled::before,
.multiselect--disabled::after {
  display: none !important;
}

.premium-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 9px;
  margin-left: 8px;
  background: rgba(248, 203, 0, 0.12);
  border: 1px solid rgba(248, 203, 0, 0.4);
  color: #f8cb00;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 999px;
  text-decoration: none;
  vertical-align: middle;
}
.premium-pill:hover {
  background: rgba(248, 203, 0, 0.2);
  color: #ffd83d;
  text-decoration: none;
}
.premium-pill i {
  font-size: 9px;
}
.premium-hint {
  font-size: 13px;
  color: #b8b6c8;
  margin: 6px 0 10px;
  font-style: italic;
}
.premium-hint a {
  color: #f8cb00;
  font-weight: 600;
  font-style: normal;
  text-decoration: none;
  margin-left: 4px;
}
.premium-hint a:hover {
  color: #ffd83d;
  text-decoration: none;
}

.smalltitle {
    margin-bottom: 0;
}

.c-switch {
    position: relative;
    top: 1px;
}


</style>
